/*
 * Copyright (c) 2024-2026 Nodals AI Ltd
 * All rights reserved.
 */

import { Logger } from '../../../../logger';
import { generateUUID, getGlobalPrebidObj } from '../../../../util';
import { SiteConfig } from '../../../config';
import { BaseVideoPlacement, Quartile } from '../index';

import { CampaignType, type CampaignData, type RenderedAdData } from '../../../../types/index';
import type { PrebidAdUnit, PrebidBidResponse } from '../../../../types/prebid';
import type {
  JwPlayer,
  JwPlayerAdsManagerEvent,
  JwPlayerGoogImaPlugin,
  JwPlayerWindow,
} from '../../../../types/vendor/jwplayer';

const logger = Logger.getInstance();


// Event through which JW's googima plugin hands out the live IMA AdsManager.
const ADS_MANAGER_EVENT = 'adsManager';

// The inline VAST <Ad id> of a Prebid video bid, used to recognise which of the
// ads IMA plays was won by a Prebid bidder (see docs/JWPLAYER.md).
const VAST_AD_ID_REGEX = /<Ad[^>]*\sid="([^"]*)"/;

// Shortest VAST <Ad id> that can safely be used as a match key. Observed live:
// PubMatic returns an id of "1", and GAM wrapper chains contain the literal
// "default" — either would collide with unrelated ads and attribute a bid's
// advertiser domains to an ad that bidder never won. Since the domains are the
// entire value of a programmatic capture, a wrong one is worse than none, so
// weak ids are dropped rather than matched loosely.
const MIN_MATCHABLE_VAST_AD_ID_LENGTH = 8;


// The plugin name JW registers the Google IMA client under.
const GOOGIMA_PLUGIN_NAME = 'googima';

// Selector for a JW player container. JW puts `jwplayer` on the container it
// owns and `jw-plugin`/`jw-ads-view` on its inner plugin divs, so this matches
// the container alone (verified live).
const PLAYER_CONTAINER_SELECTOR = 'div.jwplayer[id]';

// The player is embedded by publisher JS (a carousel/recommendation widget)
// rather than server-rendered markup, so it can appear well after the base
// class's own slot-detection window (6 x 500ms) has closed. These bound a
// longer discovery poll — 10s total — before giving up.
const PLAYER_DISCOVERY_INTERVAL_MS = 500;
const MAX_PLAYER_DISCOVERY_ATTEMPTS = 20;

// Bounds the late-attach fallback poll that reads an AdsManager already
// created before this placement subscribed — 10s total.
const ADS_MANAGER_POLL_INTERVAL_MS = 250;
const MAX_ADS_MANAGER_POLL_ATTEMPTS = 40;

/**
 * Video placement for publishers running JW Player 8 with the Google IMA
 * client (`advertising.client = 'googima'`).
 *
 * Ad tracking is driven off the IMA `AdsManager` rather than JW's own ad
 * events: IMA exposes the full VAST wrapper chain, which the direct-sold
 * classification depends on, and it provides PAUSED/RESUMED, which the
 * viewable-impression continuity clock needs. That makes the event handling
 * here deliberately identical to `DigitekaVideoPlacement` — only the way the
 * `AdsManager` is obtained differs (see docs/JWPLAYER.md).
 */
class JwPlayerVideoPlacement extends BaseVideoPlacement {
  private jwWindow: JwPlayerWindow;
  private player: JwPlayer | null = null;
  private adsManagerSubscribed: boolean = false;
  private adsManagerHandler: ((event: JwPlayerAdsManagerEvent) => void) | null = null;

  // JW tears down and recreates the AdsManager between ad breaks, so a single
  // "listeners attached" latch would silently stop tracking after the first
  // break. Identity of each manager already wired is tracked instead, which
  // both re-binds on a new manager and stays idempotent if the same manager is
  // delivered twice (the event and the poll fallback can race).
  private wiredAdsManagers: WeakSet<google.ima.AdsManager> = new WeakSet();

  // Inline VAST <Ad id> -> the Prebid video bid that carried it. Rebuilt per ad
  // break; used to tell a Prebid-won ad from a GAM-trafficked one.
  private prebidBidsByVastAdId: Map<string, PrebidBidResponse> = new Map();

  private playerDiscoveryIntervalId: ReturnType<typeof setInterval> | null = null;
  private playerDiscoveryAttempts: number = 0;
  private adsManagerPollIntervalId: ReturnType<typeof setInterval> | null = null;
  private adsManagerPollAttempts: number = 0;

  constructor(
    adUnit: PrebidAdUnit,
    adUnitTransformer: (adUnitCode: string) => string,
    campaignData: CampaignData[],
    siteConfig: SiteConfig,
  ) {
    super(adUnit, adUnitTransformer, campaignData, siteConfig);
    this.jwWindow = window as JwPlayerWindow;
  }

  /**
   * The slot is the player's own container, taken from `getContainer()` rather
   * than a DOM lookup so it is always the element JW actually owns.
   *
   * `adUnit.video.divId` is honoured when the publisher supplies it, but unlike
   * the other video placements it cannot be relied on: a JW ad unit is
   * frequently declared with `mediaTypes.video` alone and no `video.divId`
   * (verified live), so the container is discovered from the DOM otherwise.
   */
  protected setSlot(): void {
    if (this.slot !== null) {
      return;
    }

    const player = this.resolvePlayer();
    if (!player) {
      return; // Not embedded yet; discovery polling will re-enter.
    }

    const container = player.getContainer?.() ?? null;
    if (!container) {
      return;
    }

    this.player = player;
    this.slot = container;
  }

  protected setSupportedSizes(): void {}

  /**
   * Extends slot detection beyond the base class's 3s window. The base class
   * is re-entered (rather than assigning `this.slot` here) so that its
   * slot-found sequence — dimensions, visibility, listeners — runs exactly as
   * it does for every other placement. `setSlot()` remains the only writer of
   * `this.slot`.
   */
  public determineSlot(): void {
    super.determineSlot();

    if (this.slot) {
      this.clearPlayerDiscoveryInterval();
      return;
    }

    this.startPlayerDiscoveryInterval();
  }

  /**
   * When floating is enabled JW pins its `.jw-wrapper` child as a fixed
   * mini-player while the container keeps its place in document flow (the
   * container carries `jw-flag-floating`; the wrapper is what moves). Measuring
   * the container would therefore report the player as out of view for the
   * whole of a floated playback, so the wrapper — which tracks the visible
   * player in both states — is measured instead. Floated playback counts as in
   * view, matching the Dailymotion docked-player treatment.
   *
   * The class name is JW's to rename, so fall back to the container itself.
   */
  protected getViewabilityElement(): HTMLElement | null {
    if (!this.slot) {
      return null;
    }
    const container = this.slot as HTMLElement;
    return (container.querySelector('.jw-wrapper') as HTMLElement | null) ?? container;
  }

  protected addListeners(): void {
    this.subscribeToAdsManager();
  }

  /**
   * Clicks arrive as IMA CLICK events, wired in `wireAdsManager()`.
   */
  protected addAdInteractionListener(): void {}

  public destroy(): void {
    this.clearPlayerDiscoveryInterval();
    this.clearAdsManagerPollInterval();
    this.unsubscribeFromAdsManager();
    super.destroy();
  }

  /**
   * The player outlives this placement, so its listener list would otherwise
   * keep a destroyed placement reachable through the handler closure.
   */
  private unsubscribeFromAdsManager(): void {
    const handler = this.adsManagerHandler;
    if (!handler) {
      return;
    }

    try {
      this.player?.off?.(ADS_MANAGER_EVENT, handler);
      this.getGoogImaPlugin()?.off?.(ADS_MANAGER_EVENT, handler);
    } catch (error) {
      logger.warn(`Failed to unsubscribe from JW ${ADS_MANAGER_EVENT} event: ${error}`);
    }

    this.adsManagerHandler = null;
    this.adsManagerSubscribed = false;
  }

  private resolvePlayer(): JwPlayer | null {
    const jwplayer = this.jwWindow.jwplayer;
    if (typeof jwplayer !== 'function') {
      return null;
    }

    const divId = this.adUnit?.video?.divId;
    if (divId) {
      return this.playerFor(jwplayer, divId);
    }

    const container = document.querySelector(PLAYER_CONTAINER_SELECTOR);
    if (!container?.id) {
      return null;
    }

    return this.playerFor(jwplayer, container.id);
  }

  /**
   * `jwplayer(id)` returns an API object even for an id it does not know, so a
   * resolvable container is what distinguishes a set-up player from a stub.
   */
  private playerFor(
    jwplayer: NonNullable<JwPlayerWindow['jwplayer']>,
    divId: string,
  ): JwPlayer | null {
    try {
      const player = jwplayer(divId);
      return player?.getContainer?.() ? player : null;
    } catch (error) {
      logger.warn(`Failed to resolve JW player for divId ${divId}: ${error}`);
      return null;
    }
  }

  private startPlayerDiscoveryInterval(): void {
    if (this.playerDiscoveryIntervalId !== null) {
      return;
    }

    this.playerDiscoveryAttempts = 0;
    this.playerDiscoveryIntervalId = setInterval(() => {
      this.playerDiscoveryAttempts++;

      if (this.slot) {
        this.clearPlayerDiscoveryInterval();
        return;
      }

      if (this.playerDiscoveryAttempts >= MAX_PLAYER_DISCOVERY_ATTEMPTS) {
        this.clearPlayerDiscoveryInterval();
        logger.warn(`No JW player found for adUnit ${this.adUnitCode}`);
        return;
      }

      // Only re-enter the base class once the player can actually be resolved:
      // a slotless `determineSlot()` restarts the base's own detection interval,
      // so polling it directly would keep that timer alive (and re-log its
      // warning) for the whole of this longer window.
      if (this.resolvePlayer()) {
        this.determineSlot();
      }
    }, PLAYER_DISCOVERY_INTERVAL_MS);
  }

  private clearPlayerDiscoveryInterval(): void {
    if (this.playerDiscoveryIntervalId !== null) {
      clearInterval(this.playerDiscoveryIntervalId);
      this.playerDiscoveryIntervalId = null;
    }
  }

  private getGoogImaPlugin(): JwPlayerGoogImaPlugin | null {
    try {
      return this.player?.getPlugin?.(GOOGIMA_PLUGIN_NAME) ?? null;
    } catch (error) {
      logger.warn(`Failed to obtain JW googima plugin: ${error}`);
      return null;
    }
  }

  /**
   * Subscribes to the `adsManager` event on both the player and the googima
   * plugin. The plugin is what triggers the event; whether the player re-emits
   * it publicly varies by JW build, so both are subscribed and the resulting
   * duplicate delivery is absorbed by `wiredAdsManagers`.
   */
  private subscribeToAdsManager(): void {
    if (this.adsManagerSubscribed || !this.player) {
      return;
    }

    const handler = (event: JwPlayerAdsManagerEvent): void => this.handleAdsManagerEvent(event);
    this.adsManagerHandler = handler;

    try {
      this.player.on?.(ADS_MANAGER_EVENT, handler);
    } catch (error) {
      logger.warn(`Failed to subscribe to JW player ${ADS_MANAGER_EVENT} event: ${error}`);
    }

    const plugin = this.getGoogImaPlugin();
    if (plugin?.on) {
      try {
        plugin.on(ADS_MANAGER_EVENT, handler);
      } catch (error) {
        logger.warn(`Failed to subscribe to JW googima ${ADS_MANAGER_EVENT} event: ${error}`);
      }
    }

    this.adsManagerSubscribed = true;
    logger.debug(`Subscribed to JW ${ADS_MANAGER_EVENT} for ${this.adUnitCode}`);

    // Covers the case where the first ad break was already under way before
    // this placement had a slot, so the event has been and gone.
    this.startAdsManagerPollInterval();
  }

  private handleAdsManagerEvent(event: JwPlayerAdsManagerEvent): void {
    const adsManager = event?.adsManager;
    if (!adsManager) {
      logger.warn(`JW ${ADS_MANAGER_EVENT} event carried no AdsManager`);
      return;
    }
    this.wireAdsManager(adsManager);
  }

  private startAdsManagerPollInterval(): void {
    if (this.adsManagerPollIntervalId !== null) {
      return;
    }

    this.adsManagerPollAttempts = 0;
    this.adsManagerPollIntervalId = setInterval(() => {
      this.adsManagerPollAttempts++;

      const adsManager = this.getGoogImaPlugin()?.adsLoader?.adsManager;
      if (adsManager) {
        this.wireAdsManager(adsManager);
        return;
      }

      if (this.adsManagerPollAttempts >= MAX_ADS_MANAGER_POLL_ATTEMPTS) {
        this.clearAdsManagerPollInterval();
      }
    }, ADS_MANAGER_POLL_INTERVAL_MS);
  }

  private clearAdsManagerPollInterval(): void {
    if (this.adsManagerPollIntervalId !== null) {
      clearInterval(this.adsManagerPollIntervalId);
      this.adsManagerPollIntervalId = null;
    }
  }

  private wireAdsManager(adsManager: google.ima.AdsManager): void {
    if (this.wiredAdsManagers.has(adsManager)) {
      return;
    }

    const adEventType = this.jwWindow.google?.ima?.AdEvent?.Type;
    if (!adEventType) {
      logger.warn('Google IMA SDK is not available on the page');
      return;
    }

    if (typeof adsManager.addEventListener !== 'function') {
      logger.warn('JW AdsManager does not expose addEventListener');
      return;
    }

    this.wiredAdsManagers.add(adsManager);
    this.clearAdsManagerPollInterval();

    // LOADED fires per ad, before STARTED, which is the natural point to
    // snapshot the Prebid video bids for this break.
    adsManager.addEventListener(adEventType.LOADED, () => this.refreshPrebidVideoBids());
    adsManager.addEventListener(adEventType.STARTED, (event: google.ima.AdEvent) =>
      this.adStartedHandler(event),
    );
    adsManager.addEventListener(adEventType.COMPLETE, (event: google.ima.AdEvent) =>
      this.adCompleteHandler(event),
    );
    adsManager.addEventListener(adEventType.FIRST_QUARTILE, () =>
      this.dispatchAdQuartileEvent(Quartile.FIRST),
    );
    adsManager.addEventListener(adEventType.MIDPOINT, () =>
      this.dispatchAdQuartileEvent(Quartile.SECOND),
    );
    adsManager.addEventListener(adEventType.THIRD_QUARTILE, () =>
      this.dispatchAdQuartileEvent(Quartile.THIRD),
    );
    adsManager.addEventListener(adEventType.PAUSED, () => this.pauseViewabilityTracking());
    adsManager.addEventListener(adEventType.RESUMED, () => this.resumeViewabilityTracking());
    adsManager.addEventListener(adEventType.SKIPPED, () => this.adSkippedHandler());
    adsManager.addEventListener(adEventType.CLICK, () => this.adClickedHandler());

    logger.debug(`JW IMA event listeners registered for ${this.adUnitCode}`);
  }

  /**
   * Snapshots the Prebid video bids for this ad unit, keyed by the inline VAST
   * `<Ad id>` each one carries. Prebid tracks responses under the original
   * (untransformed) ad unit code.
   */
  private refreshPrebidVideoBids(): void {
    const pbjs = getGlobalPrebidObj();
    if (!pbjs?.getBidResponsesForAdUnitCode) {
      return;
    }

    this.prebidBidsByVastAdId.clear();
    const bids = pbjs.getBidResponsesForAdUnitCode(this.adUnit.code)?.bids ?? [];
    for (const bid of bids) {
      if (bid.mediaType !== 'video') {
        continue;
      }
      if (!bid.vastXml) {
        logger.debug(`Skipping ${bid.bidder} video bid without inline VAST for ${this.adUnitCode}`);
        continue;
      }
      const vastAdId = bid.vastXml.match(VAST_AD_ID_REGEX)?.[1];
      if (!vastAdId) {
        continue;
      }
      if (vastAdId.length < MIN_MATCHABLE_VAST_AD_ID_LENGTH) {
        logger.debug(
          `Skipping ${bid.bidder} video bid for ${this.adUnitCode}: VAST ad id "${vastAdId}" is too weak to match on`,
        );
        continue;
      }
      this.prebidBidsByVastAdId.set(vastAdId, bid);
    }
  }

  /**
   * Finds the Prebid bid that won this ad, if any.
   *
   * A Prebid video win is usually delivered through a GAM header-bidding line
   * item, so the ad arrives wrapped and the *inline* id is the bidder's while
   * the outer ids are GAM's. IMA exposes the whole chain, so every id in it is
   * tried — this is what `docs/DAILYMOTION.md` flags as unsolvable there, where
   * only one ad id is surfaced.
   */
  private findPrebidBidForAd(ad: google.ima.Ad): PrebidBidResponse | undefined {
    if (this.prebidBidsByVastAdId.size === 0) {
      // Covers listeners attached mid-break, so LOADED was missed.
      this.refreshPrebidVideoBids();
    }
    if (this.prebidBidsByVastAdId.size === 0) {
      return undefined;
    }

    const candidateIds = [ad.getAdId(), ...ad.getWrapperAdIds()];
    for (const id of candidateIds) {
      if (!id) {
        continue;
      }
      const bid = this.prebidBidsByVastAdId.get(id);
      if (bid) {
        return bid;
      }
    }
    return undefined;
  }

  /**
   * A Prebid-won ad is only worth recording when the bid identifies the buyer:
   * without advertiser domains there is nothing to attribute it to, so it is
   * dropped rather than recorded as unattributable volume. This mirrors both
   * the display path and `DailyMotionVideoPlacement`.
   *
   * Dispatch is inline because pbjs `adRenderSucceeded` never fires for ads
   * rendered by GAM/IMA inside the player, so the deferred display flow
   * (`handleRenderedAdData` with `isPrebidAd: true`) would wait forever.
   */
  private handlePrebidWonAd(bid: PrebidBidResponse, ad: google.ima.Ad): void {
    const advertiserDomains = this.cleanAdvertiserDomains(bid.meta?.advertiserDomains ?? []);
    if (advertiserDomains.length === 0) {
      logger.debug(
        `Ignoring Prebid-won JW ad for ${this.adUnitCode}: no valid advertiser domains`,
      );
      return;
    }

    logger.debug(
      `JW ad matched Prebid ${bid.bidder} bid for ${this.adUnitCode} via VAST ad id`,
    );

    this.adCurrentlyPlaying = true;

    const duration = ad.getDuration();
    const nodalsDealId = bid.dealId && this.hasDealActivated(bid.dealId) ? bid.dealId : undefined;

    // Deliberately no creativeId: on a programmatic capture the only useful
    // identifier is the advertiser domain. A bidder's creative id is not
    // resolvable against GAM, and `ProgrammaticServedAdImpression` does not
    // carry one — setting it would imply a link that does not exist.
    this.renderedAdData = {
      advertiserDomains,
      durationSeconds: duration > 0 ? Math.round(duration) : undefined,
      nodalsDealId,
      isPrebidAd: true,
      isProgrammaticBuy: true,
      responseIdentifier: generateUUID(),
      size: { width: this.playerWidth, height: this.playerHeight },
    };

    const deliveryId = nodalsDealId
      ? this.getDealCampaignDeliveryId(nodalsDealId)
      : this.getLearningCampaignDeliveryId(CampaignType.LEARNING_PROGRAMMATIC);
    if (deliveryId) {
      this.dispatchProgrammaticServedAdImpression(deliveryId);
    }
  }

  /**
   * Mirrors the Digiteka classification: only GAM direct-sold ads are tracked,
   * identified by the ad system of the outermost VAST wrapper.
   *
   * AdSystem == 'GDFP' AND Wrapper Ad Systems is empty
   * OR
   * AdSystem != 'GDFP' AND Wrapper Ad Systems is NOT empty AND last Wrapper Ad
   * Systems == 'GDFP' AND advertiser name is ''
   */
  private isDirectSoldAd(ad: google.ima.Ad): boolean {
    const adSystem = ad.getAdSystem();
    const wrapperAdSystems = ad.getWrapperAdSystems();
    const advertiserName = ad.getAdvertiserName();

    if (adSystem === 'GDFP' && wrapperAdSystems.length === 0) {
      return true;
    }

    if (
      adSystem !== 'GDFP' &&
      wrapperAdSystems.length > 0 &&
      wrapperAdSystems[wrapperAdSystems.length - 1] === 'GDFP' &&
      advertiserName === ''
    ) {
      return true;
    }

    return false;
  }

  private adStartedHandler(event: google.ima.AdEvent): void {
    if (this.adCurrentlyPlaying) {
      return;
    }

    this.adStarted();

    const ad = event.getAd();
    if (!ad) {
      logger.warn('Ad object is null in adStartedHandler event');
      return;
    }

    logger.debug(
      `JW Ad Started event received - AdId: ${ad.getAdId()}, AdSystem: ${ad.getAdSystem()}, WrapperAdSystems: [${ad.getWrapperAdSystems().join(', ')}], AdvertiserName: ${ad.getAdvertiserName()}, CreativeId: ${ad.getCreativeId()}`,
    );

    const currentOpportunity = this.currentOpportunity;
    if (currentOpportunity && this.completedOpportunityIds.has(currentOpportunity.id)) {
      return;
    }

    // A Prebid win is checked for first. Those ads are delivered through GAM
    // header-bidding line items, so the outermost ad system is GDFP with no
    // advertiser name — indistinguishable from a GAM-trafficked ad by the
    // wrapper chain alone, and therefore accepted by isDirectSoldAd(). The bid
    // match is what separates them, and it carries the advertiser domains that
    // make the ad attributable.
    const prebidBid = this.findPrebidBidForAd(ad);
    if (prebidBid) {
      this.handlePrebidWonAd(prebidBid, ad);
      return;
    }

    if (!this.isDirectSoldAd(ad)) {
      // Exchange-bought: no GAM-resolvable creative id, but the clickthrough
      // chain often identifies the buyer.
      if (this.captureExchangeAdByClickThrough(ad.getTraffickingParametersString(), ad.getDuration())) {
        return;
      }
      logger.debug('Ad is not a direct sold ad and carries no clickthrough domain, ignoring');
      return;
    }

    this.adCurrentlyPlaying = true;

    // Outermost wrapper identifiers describe the GAM line/creative that served
    // the ad; the inline ids describe whatever the wrapper redirected to.
    //
    // An unwrapped GAM direct-sold ad carries no VAST <Ad id> and IMA surfaces
    // it as the string '0' (verified live against a previewed GAM creative on
    // Future's network). That is not a line item id: passing it on would put
    // junk in the impression payload and could false-match a monitored line id,
    // so it is dropped and attribution falls to the creative id alone.
    const wrapperAdIds = ad.getWrapperAdIds();
    const inlineAdId = ad.getAdId();
    const lineItemId =
      wrapperAdIds.length > 0
        ? wrapperAdIds[wrapperAdIds.length - 1]
        : inlineAdId === '0'
          ? null
          : inlineAdId;

    const wrapperCreativeIds = ad.getWrapperCreativeIds();
    const creativeId =
      wrapperCreativeIds && wrapperCreativeIds.length > 0
        ? wrapperCreativeIds[wrapperCreativeIds.length - 1]
        : ad.getCreativeId();

    const renderedAdData: RenderedAdData = {
      lineItemId: lineItemId || undefined,
      creativeId: creativeId || undefined,
      // Nameless direct ads may be pass-through creatives; a resolvable click
      // chain names the actual buyer next to the creative id (see
      // clickThroughAdvertiserDomains). Named ads are attributed by the ad
      // server's own advertiser and never overridden from the chain.
      advertiserDomains:
        ad.getAdvertiserName() === ''
          ? this.clickThroughAdvertiserDomains(ad.getTraffickingParametersString())
          : undefined,
      durationSeconds: ad.getDuration() ? Math.round(ad.getDuration()) : undefined,
      nodalsDealId: ad.getDealId() || undefined,
      isPrebidAd: false,
      isProgrammaticBuy: false,
      responseIdentifier: generateUUID(),
      size: { width: this.playerWidth, height: this.playerHeight },
    };

    this.handleRenderedAdData(renderedAdData);
  }

  private adCompleteHandler(_event: google.ima.AdEvent): void {
    const currentOpportunity = this.currentOpportunity;
    if (!currentOpportunity) {
      return;
    }

    if (this.completedOpportunityIds.has(currentOpportunity.id)) {
      return;
    }

    logger.debug('JW Ad Complete event received');

    this.dispatchAdQuartileEvent(Quartile.FOURTH);
    this.adCurrentlyPlaying = false;
    // Note: completedOpportunityIds is set inside dispatchAdQuartileEvent when FOURTH quartile is dispatched
  }

  private adSkippedHandler(): void {
    logger.debug('JW Ad Skipped event received');
    this.adCurrentlyPlaying = false;
  }

  private adClickedHandler(): void {
    if (!this.adCurrentlyPlaying) {
      return;
    }

    logger.debug('JW Ad Clicked event received');

    this.dispatchAdInteraction();
  }
}

export { JwPlayerVideoPlacement };
